import { createContext, useContext, useReducer, ReactNode } from 'react';
import { EVMData } from '@/types/evm';

interface EVMState {
  data: EVMData;
  isDirty: boolean;
}

type EVMAction = 
  | { type: 'UPDATE_DATA'; payload: Partial<EVMData> }
  | { type: 'CLEAR_DATA' }
  | { type: 'MARK_SAVED' };

const initialEVMData: EVMData = {
  projectName: '',
  clientName: '',
  startDate: null,
  endDate: null,
  reportingPeriodStart: null,
  reportingPeriodEnd: null,
  bac: null,
  ac: null,
  pv: null,
  ev: null,
};

const initialState: EVMState = {
  data: initialEVMData,
  isDirty: false
};

const evmReducer = (state: EVMState, action: EVMAction): EVMState => {
  switch (action.type) {
    case 'UPDATE_DATA':
      return {
        data: { ...state.data, ...action.payload },
        isDirty: true
      };
    case 'CLEAR_DATA':
      return initialState;
    case 'MARK_SAVED':
      return {
        ...state,
        isDirty: false
      };
    default:
      return state;
  }
};

const EVMContext = createContext<{
  state: EVMState;
  dispatch: React.Dispatch<EVMAction>;
} | null>(null);

export function EVMProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(evmReducer, initialState);

  return (
    <EVMContext.Provider value={{ state, dispatch }}>
      {children}
    </EVMContext.Provider>
  );
}

export function useEVM() {
  const context = useContext(EVMContext);
  if (!context) {
    throw new Error('useEVM must be used within EVMProvider');
  }
  return context;
}